import React, { useState, useEffect, useCallback } from "react";
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
  NOSPACE_REGEX,
  PASSWORDS_REGEX,
} from "../../utils/constants";
import { MESSAGE } from "../../utils/validationMessage";
import * as Yup from "yup";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { availablePermissions } from "./availablePermission";
import ApiService from "../../services/categoryManagementApi";

const { subAdminServcie } = ApiService;

function AddEditSubadmin() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [data, setData] = useState(null);

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await subAdminServcie.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  const subadminSchema =  Yup.object().shape({
    name: Yup.string()
    .max(60, "Name should not be longer than 60 characters")
    .required(MESSAGE.REQUIRED)
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE),
    permission: Yup.array()
      .of(Yup.string())
      .min(1, "At least one permission is required")
      .required("Permissions are required"),
  });

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  useEffect(() => {
    if (id && data?.permission) {
      setPermissions(data.permission);
    }
  }, [id, data]);
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: subadminSchema,
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
      permission: Object.keys(data?.permission || {}) || [],
    },
    validate: (values) => {
      const errors = {};
      const email = values.email.trim();

      if (!email) {
        errors.email = "This field is required";
      } else if (!EMAIL_REGEX.test(email)) {
        errors.email = "Invalid email address";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const permissionsObject = Object.keys(permissions).reduce(
          (acc, permission) => {
            acc[permission] = permissions[permission];
            return acc;
          },
          {}
        );

        const payload = {
          id: id || undefined,
          name: values.name.trim(),
          email: values.email,
          permission: permissionsObject,
        };

        const response = id
          ? await subAdminServcie.edit(payload)
          : await subAdminServcie.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;
  const handleEmailChange = (event) => {
    const { value } = event.target;

    const trimmedValue = value.replace(/^\s+|\s+(?=\s)|\s+$/g, "");
    formik.setFieldValue("email", trimmedValue);
  };
  return (
    <Box>
      {/* <Helmet> */}
        <title>{id ? "Edit" : "Add"} Subadmin</title>
      {/* </Helmet> */}
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Subadmin`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={formik.values.email}
                    onChange={handleEmailChange}
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                {/* {!id &&
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    required
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    inputProps={{ minLength: 8 }}
                  />
                </Grid>
} */}
                <Grid item xs={6} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Permissions"
                    name="permissions"
                    value={Object.keys(permissions)}
                    onChange={(e) => {
                      const selectedPermissions = e.target.value;
                      setPermissions(
                        selectedPermissions.reduce((acc, permission) => {
                          acc[permission] = true;
                          return acc;
                        }, {})
                      );
                      formik.setFieldTouched("permission", true);
                      formik.setFieldValue("permission", selectedPermissions);
                    }}
                    error={Boolean(
                      formik.touched.permission && formik.errors.permission
                    )}
                    helperText={
                      formik.touched.permission && formik.errors.permission
                    }
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => selected.join(", "),
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 300, 
                            overflowY: "auto",
                          },
                        },
                      },
                    }}
                    sx={{
                      // Customize input font size
                      '& .MuiInputBase-input': {
                        fontSize: '13.8px',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '12px',
                      },
                    }}
                  >
                    {Object.keys(availablePermissions).map((permission) => (
                      <MenuItem key={permission} value={permission}>
                        <Checkbox
                          checked={Object.keys(permissions).includes(
                            permission
                          )}
                        />
                        <ListItemText primary={permission} />
                      </MenuItem>
                    ))}
                  </TextField>

     

                </Grid>
                {
        Object.keys(permissions).length > 0 && 
        <Grid item xs={12} md={12}>
          <Box
            sx={{
              marginTop: "12px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              // backgroundColor: "#f9f9f9",
              fontSize: "14px",
              width:"100%"
            }}
          >
            <strong>Selected Permissions:</strong>{" "}
            { Object.keys(permissions).length > 0
              ? Object.keys(permissions).join(", ")
              : "None"}
          </Box>
            </Grid>
              }         
                <Grid item xs={12} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      Send invitation
                    </LoadingButton>
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}></Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditSubadmin;
